/**
 * Thread Assignment Dialog
 * 
 * Dialog that shows thread details and allows the user to assign themselves to the thread.
 */

import React, { useState } from 'react';
import { Modal, Form, Input, Button, Descriptions, Tag, Space, message } from 'antd';
import { UserOutlined, LinkOutlined } from '@ant-design/icons';

interface ThreadAssignmentDialogProps {
  visible: boolean;
  threadData: {
    id: string;
    name: string;
    description?: string;
    currentUserId?: string;
    tags?: string[];
    context?: any;
    created_at?: string;
  } | null;
  currentUser: {
    id: string;
    username: string;
    fullName?: string;
  } | null;
  onConfirm: (threadId: string, userId: string) => Promise<void>;
  onCancel: () => void;
}

export const ThreadAssignmentDialog: React.FC<ThreadAssignmentDialogProps> = ({
  visible,
  threadData,
  currentUser,
  onConfirm,
  onCancel
}) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const handleConfirm = async () => {
    if (!threadData || !currentUser) return;

    try {
      setLoading(true);
      await onConfirm(threadData.id, currentUser.id);
      message.success('Thread assigned successfully!');
      form.resetFields();
    } catch (error) {
      message.error('Failed to assign thread');
      console.error('Thread assignment error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  if (!threadData) return null;

  return (
    <Modal
      title={
        <Space>
          <LinkOutlined />
          <span>Assign Thread to User</span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      width={600}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="confirm"
          type="primary"
          loading={loading}
          onClick={handleConfirm}
          icon={<UserOutlined />}
        >
          Assign to Current User
        </Button>
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <p>
          This thread needs to be assigned to a user before it can be displayed in the Exo Panel.
          Click "Assign to Current User" to assign this thread to your account.
        </p>
      </div>

      <Descriptions
        title="Thread Details"
        bordered
        column={1}
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Descriptions.Item label="Thread ID">
          <code style={{ fontSize: '12px' }}>{threadData.id}</code>
        </Descriptions.Item>
        <Descriptions.Item label="Name">
          {threadData.name}
        </Descriptions.Item>
        <Descriptions.Item label="Description">
          {threadData.description || 'No description'}
        </Descriptions.Item>
        <Descriptions.Item label="Current User ID">
          {threadData.currentUserId ? (
            <code style={{ fontSize: '12px' }}>{threadData.currentUserId}</code>
          ) : (
            <Tag color="red">Not assigned</Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Tags">
          {threadData.tags && threadData.tags.length > 0 ? (
            <Space wrap>
              {threadData.tags.map((tag, index) => (
                <Tag key={index} color="blue">{tag}</Tag>
              ))}
            </Space>
          ) : (
            'No tags'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Created">
          {threadData.created_at ? new Date(threadData.created_at).toLocaleString() : 'Unknown'}
        </Descriptions.Item>
      </Descriptions>

      <Descriptions
        title="Assignment Details"
        bordered
        column={1}
        size="small"
      >
        <Descriptions.Item label="Current User">
          {currentUser ? (
            <Space>
              <UserOutlined />
              <span>{currentUser.fullName || currentUser.username}</span>
              <code style={{ fontSize: '12px' }}>({currentUser.id})</code>
            </Space>
          ) : (
            <Tag color="red">No user logged in</Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Action">
          <Tag color="green">Assign thread to current user</Tag>
        </Descriptions.Item>
      </Descriptions>

      {threadData.context && (
        <div style={{ marginTop: 16 }}>
          <h4>Thread Context:</h4>
          <pre style={{ 
            backgroundColor: '#f5f5f5', 
            padding: 12, 
            borderRadius: 4, 
            fontSize: '12px',
            maxHeight: 200,
            overflow: 'auto'
          }}>
            {JSON.stringify(threadData.context, null, 2)}
          </pre>
        </div>
      )}
    </Modal>
  );
};

export default ThreadAssignmentDialog;
